import React, { useEffect, useState } from 'react';
import { X, UserPlus, MessageSquare, Bell, Settings, Check, AtSign } from 'lucide-react';
import { format } from 'date-fns';
import { useNotificationStore } from '../../store/notificationStore';
import { useFriendStore } from '../../store/friendStore';
import { useServerStore } from '../../store/serverStore';
import { useMessageStore } from '../../store/messageStore';
import { friendsAPI } from '../../lib/api';

interface NotificationCenterProps {
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onClose }) => {
  const { 
    notifications, 
    fetchNotifications, 
    markAsRead, 
    markAllAsRead, 
    removeNotification,
    isLoading
  } = useNotificationStore();
  
  const { acceptFriendRequest, rejectFriendRequest } = useFriendStore();
  const { joinServer, setCurrentServer, setCurrentChannel } = useServerStore();
  const { setCurrentDmUser } = useMessageStore();
  
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'mentions'>('all');
  
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);
  
  const filteredNotifications = notifications.filter(notification => {
    if (activeTab === 'unread') return !notification.read;
    if (activeTab === 'mentions') return notification.type === 'mention';
    return true;
  });
  
  const handleAcceptFriendRequest = async (notificationId: string, userId: string) => {
    try {
      // D'abord, récupérer l'ID de la demande d'ami
      const friendRequests = await friendsAPI.getPendingRequests();
      const request = friendRequests.find(req => req.user_id === userId);
      
      if (request) {
        await acceptFriendRequest(request.id);
        await markAsRead(notificationId);
      } else {
        console.error('Friend request not found');
      }
    } catch (error) {
      console.error('Failed to accept friend request:', error);
    }
  };
  
  const handleRejectFriendRequest = async (notificationId: string, userId: string) => {
    try {
      // D'abord, récupérer l'ID de la demande d'ami
      const friendRequests = await friendsAPI.getPendingRequests();
      const request = friendRequests.find(req => req.user_id === userId);
      
      if (request) {
        await rejectFriendRequest(request.id);
        await markAsRead(notificationId);
      } else {
        console.error('Friend request not found');
      }
    } catch (error) {
      console.error('Failed to reject friend request:', error);
    }
  };
  
  const handleJoinServer = async (notificationId: string, serverId: string) => {
    try {
      const server = await joinServer(serverId);
      await markAsRead(notificationId);
      
      // Fermer le centre de notifications après avoir rejoint
      onClose();
      
      return server;
    } catch (error) {
      console.error('Failed to join server:', error);
    }
  };
  
  const handleViewMessage = async (notification: any) => {
    // Marquer la notification comme lue
    await markAsRead(notification.id);
    
    // Rediriger vers le message approprié
    if (notification.channel?.id && notification.server?.id) {
      // Pour les messages de canal
      setCurrentDmUser(null);
      setCurrentServer(notification.server);
      setCurrentChannel({
        ...notification.channel,
        type: notification.channel.type || 'text',
      });
    } else if (notification.from?.id) {
      // Pour les messages directs
      setCurrentChannel(null);
      setCurrentDmUser(notification.from.id);
    }
    
    onClose();
  };
  
  const renderNotificationContent = (notification: any) => {
    switch (notification.type) {
      case 'friend_request':
        return (
          <div className="flex flex-col">
            <p className="text-text-primary">
              <span className="font-medium">{notification.from?.username || "Un utilisateur"}</span> vous a envoyé une demande d'ami
            </p>
            <div className="flex mt-2 space-x-2">
              <button 
                className="btn btn-primary py-1 px-3 text-sm"
                onClick={() => notification.from && handleAcceptFriendRequest(notification.id, notification.from.id)}
                disabled={!notification.from}
              >
                Accepter
              </button>
              <button 
                className="btn btn-secondary py-1 px-3 text-sm"
                onClick={() => notification.from && handleRejectFriendRequest(notification.id, notification.from.id)}
                disabled={!notification.from}
              >
                Refuser
              </button>
            </div>
          </div>
        );
      
      case 'message':
        return (
          <div className="cursor-pointer" onClick={() => handleViewMessage(notification)}>
            <p className="text-text-primary">
              <span className="font-medium">{notification.from?.username || "Un utilisateur"}</span> vous a envoyé un message
            </p>
            <p className="text-text-secondary text-sm mt-1 italic">"{notification.content || ""}"</p>
          </div>
        );
      
      case 'server_invite':
        return (
          <div className="flex flex-col">
            <p className="text-text-primary">
              <span className="font-medium">{notification.from?.username || "Un utilisateur"}</span> vous a invité à rejoindre <span className="font-medium">{notification.server?.name || "un serveur"}</span>
            </p>
            <div className="flex mt-2 space-x-2">
              <button 
                className="btn btn-primary py-1 px-3 text-sm"
                onClick={() => notification.server?.id && handleJoinServer(notification.id, notification.server.id)}
                disabled={!notification.server?.id}
              >
                Rejoindre
              </button>
              <button 
                className="btn btn-secondary py-1 px-3 text-sm"
                onClick={() => removeNotification(notification.id)}
              >
                Refuser
              </button>
            </div>
          </div>
        );
      
      case 'mention':
        return (
          <div className="cursor-pointer" onClick={() => handleViewMessage(notification)}>
            <p className="text-text-primary">
              <span className="font-medium">{notification.from?.username || "Un utilisateur"}</span> vous a mentionné dans <span className="font-medium">#{notification.channel?.name || "un canal"}</span>
            </p>
            <p className="text-text-secondary text-sm mt-1 italic">"{notification.content || ""}"</p>
            {notification.server && (
              <p className="text-text-muted text-xs mt-1">dans {notification.server.name}</p>
            )}
          </div>
        );
      
      case 'system':
        return (
          <div>
            <p className="text-text-primary">{notification.content || "Notification système"}</p>
          </div>
        );
      
      default:
        return <p className="text-text-primary">Nouvelle notification</p>;
    }
  };
  
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'friend_request':
        return <UserPlus className="h-5 w-5 text-accent-color" />;
      case 'message':
        return <MessageSquare className="h-5 w-5 text-accent-color" />;
      case 'mention':
        return <AtSign className="h-5 w-5 text-accent-color" />;
      case 'server_invite':
        return <Bell className="h-5 w-5 text-accent-color" />;
      default:
        return <Bell className="h-5 w-5 text-accent-color" />;
    }
  };
  
  return (
    <div className="max-h-[70vh] flex flex-col">
      <div className="p-3 border-b border-border-color flex justify-between items-center">
        <h3 className="font-semibold text-text-primary">Notifications</h3>
        <div className="flex items-center space-x-2">
          <button 
            className="text-text-secondary hover:text-text-primary transition-colors"
            onClick={() => markAllAsRead()}
            title="Marquer tout comme lu"
          >
            <Check className="h-4 w-4" />
          </button>
          <button 
            className="text-text-secondary hover:text-text-primary transition-colors"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      <div className="border-b border-border-color">
        <div className="flex">
          <button
            className={`flex-1 py-2 text-center text-sm transition-colors ${
              activeTab === 'all' 
                ? 'text-text-primary border-b-2 border-accent-color' 
                : 'text-text-secondary hover:text-text-primary'
            }`}
            onClick={() => setActiveTab('all')}
          >
            Tout
          </button>
          <button
            className={`flex-1 py-2 text-center text-sm transition-colors ${
              activeTab === 'unread' 
                ? 'text-text-primary border-b-2 border-accent-color' 
                : 'text-text-secondary hover:text-text-primary'
            }`}
            onClick={() => setActiveTab('unread')}
          >
            Non lu
          </button>
          <button
            className={`flex-1 py-2 text-center text-sm transition-colors ${
              activeTab === 'mentions' 
                ? 'text-text-primary border-b-2 border-accent-color' 
                : 'text-text-secondary hover:text-text-primary'
            }`}
            onClick={() => setActiveTab('mentions')}
          >
            Mentions
          </button>
        </div>
      </div>
      
      <div className="overflow-y-auto flex-1">
        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-color"></div>
          </div>
        ) : filteredNotifications.length > 0 ? (
          filteredNotifications.map(notification => (
            <div 
              key={notification.id} 
              className={`p-3 border-b border-border-color hover:bg-highlight/50 transition-colors ${notification.read ? 'opacity-70' : ''}`}
              onClick={() => !notification.read && markAsRead(notification.id)}
            >
              <div className="flex">
                <div className="mr-3 mt-1 flex-shrink-0">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1">
                  {renderNotificationContent(notification)}
                  <p className="text-text-muted text-xs mt-2">
                    {format(new Date(notification.created_at), 'dd/MM/yyyy HH:mm')}
                  </p>
                </div>
                {!notification.read && (
                  <div className="w-2 h-2 rounded-full bg-accent-color self-start mt-2 flex-shrink-0"></div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-text-secondary">
            <Bell className="h-12 w-12 mb-2 text-text-muted" />
            <p>Pas de notifications</p>
          </div>
        )}
      </div>
      
      <div className="p-2 border-t border-border-color">
        <button 
          className="w-full text-center text-sm text-accent-color hover:text-accent-hover transition-colors"
          onClick={() => {
            // Rediriger vers les paramètres de notifications
            onClose();
          }}
        >
          <div className="flex items-center justify-center">
            <Settings className="h-3 w-3 mr-1" />
            Paramètres de notification
          </div>
        </button>
      </div>
    </div>
  );
};

export default NotificationCenter;